/* eslint-disable @typescript-eslint/no-explicit-any */
import { useAtomValue, useSetAtom } from 'jotai';
import callApi, { isCancellationResponse } from 'lib/callApi';
import { showFailureToast, showSuccessToast } from 'lib/ToastHelper';
import React, { useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import Select from 'react-select';
import { CircleSpinner } from 'react-spinners-kit';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
  Card,
  CardBody,
} from 'reactstrap';
import { companyIdAtom } from 'views/state';

import { findingGridDataRefreshAtom, selectedRowIdsAtom } from './state';

interface AssigneeModalProps {
  finding?: any;
  isOpen: boolean;
  toggle: () => void;
  isBulk?: boolean;
}

const AssigneeModal: React.FC<AssigneeModalProps> = ({
  isOpen,
  finding,
  toggle,
  isBulk = false,
}) => {
  const company_id = useAtomValue(companyIdAtom);
  const refreshFindingsGridData = useSetAtom(findingGridDataRefreshAtom);
  const selectedFindingIds = useAtomValue(selectedRowIdsAtom);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [members, setMembers] = useState([]);
  const [assignee, setAssignee] = useState<any>(null);
  const [dueDate, setDueDate] = useState<Date | null>(null);

  // Extract finding details for single finding mode
  const findingId = finding?.id;
  const sourceType = finding?.source_type;
  const title = finding?.name;
  const qid = finding?.qid;
  const ruleId = finding?.sonarqube_rule;
  const findingDueDate = finding?.due_date ? new Date(finding.due_date) : null;

  // Fetch members and current assignee
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Build URL for members API
        const url = `/profiles/colleague/new/?search=&is_active=True&nutshell=True&action=assignee&company_id=${company_id}`;

        // Fetch members
        const membersRes = await callApi(url, 'GET', {});

        if (isCancellationResponse(membersRes)) {
          console.log('Request was cancelled');
          return;
        }

        const fetchedMembers =
          membersRes.status === 200 || membersRes.status === 201
            ? membersRes.data.results
            : [];

        setMembers(fetchedMembers);

        // Fetch current assignee if exists
        if (finding?.assigned_member_profile) {
          const currentAssigneeData = fetchedMembers.find(
            (member) => member.id === parseInt(finding.assigned_member_profile),
          );
          setAssignee(currentAssigneeData);

          // Set due date if exists
          if (findingDueDate) {
            setDueDate(findingDueDate); // Ensure dueDate is initialized properly
          }
        }
      } catch (err) {
        showFailureToast(`Failed to load data. ${err}`);
        setError(`Error fetching members: ${err}`);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [company_id, finding]);

  /**
   * Handle save action for both assignment and unassignment
   * @param event - Mouse event
   * @param isAssigning - Boolean indicating if this is an assign operation (true) or unassign (false)
   */
  const handleSave = async (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    isAssigning: boolean = true,
  ): Promise<void> => {
    event.preventDefault();

    setLoading(true);
    setError('');

    try {
      // Prepare data for API call
      const data = {
        // For bulk mode, join selected IDs, otherwise use the single finding ID
        finding_ids: isBulk
          ? selectedFindingIds.join(',')
          : finding?.id.toString(),
        // If unassigning or no assignee selected, use -1, otherwise use the selected assignee's ID
        assignee_user_id: !isAssigning
          ? -1
          : assignee
            ? assignee['users_id']
            : -1,
        due_date: dueDate ? dueDate.toISOString() : null,
      };

      // Make API call
      const response = await callApi(
        '/assignee_maps/multi-vunl-assignee-map/',
        'POST',
        {},
        data,
      );

      if (response.status === 200 || response.status === 201) {
        // Show appropriate success message

        if (isAssigning) {
          showSuccessToast('Findings successfully assigned');
        } else {
          showSuccessToast('Findings successfully unassigned');
        }

        // Refresh grid data
        refreshFindingsGridData(Math.random().toString());
        // Handle success
        toggle();
      } else {
        // Handle API error

        if (isAssigning) {
          showFailureToast('Failed to assign findings');
        } else {
          showFailureToast('Failed to unassign findings');
        }
        setError('Failed to assign/unassign findings');
      }
    } catch (err) {
      // Handle exception
      showFailureToast('Failed to update finding assignment');
    } finally {
      // Reset loading states
      setLoading(false);
    }
  };

  // Assign button click handler
  const handleAssign = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    handleSave(event, true);
  };

  // Unassign button click handler
  const handleUnassign = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    handleSave(event, false);
  };

  // Select dropdown styles to ensure the menu appears above modal content
  const selectStyles = {
    control: (base) => ({
      ...base,
      cursor: 'pointer',
    }),
    option: (base) => ({
      ...base,
      cursor: 'pointer',
    }),
    menuPortal: (base: any) => ({
      ...base,
      zIndex: 9999,
    }),
  };

  // Render the form fields
  const renderFormFields = () => (
    <>
      <Row className="form-group">
        <Col xs="auto" className="d-flex align-items-center">
          <label>
            <strong>Assignee</strong>
          </label>
        </Col>
        <Col>
          <Select
            className="custom-360-dropdown profile-pic-dropdown"
            closeMenuOnSelect={true}
            isMulti={false}
            isClearable={true}
            hideSelectedOptions={true}
            onChange={(e) => {
              setAssignee(e as any);
            }}
            value={assignee}
            options={members}
            menuPortalTarget={document.body}
            styles={selectStyles}
          />
        </Col>
      </Row>
      <Row className="form-group">
        <Col xs="auto" className="d-flex align-items-center">
          <label>
            <strong>Due Date (Optional)</strong>
          </label>
        </Col>
        <Col>
          <DatePicker
            selected={dueDate}
            onChange={(date) => {
              setDueDate(date);
            }}
            timeFormat="HH:mm"
            dateFormat="MM-dd-yyyy HH:mm"
            showTimeInput
            isClearable
            className="form-control"
            placeholderText="Select Due Date"
            minDate={new Date()}
            allowSameDay={true}
            wrapperClassName="datePicker"
            popperPlacement="bottom"
            popperProps={{
              strategy: 'fixed',
            }}
          />
        </Col>
      </Row>
    </>
  );

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="md">
      <ModalHeader toggle={toggle}>Assign/Unassign Findings</ModalHeader>
      <ModalBody>
        {isBulk ? (
          <Row>
            <Col md="12">
              <>
                <strong>{selectedFindingIds?.length}</strong> findings selected
              </>
            </Col>
          </Row>
        ) : (
          <Row>
            <Col md="12">
              {' '}
              <label>
                <p>
                  <strong>Name: </strong>
                  {title}
                </p>
              </label>
            </Col>
            <Col md="6">
              <label>
                <p>
                  <strong>Finding ID: </strong>
                  {findingId}
                </p>
              </label>
            </Col>
            {sourceType === 'sast' ? (
              <Col md="6">
                <label>
                  <p>
                    <strong>Rule ID: </strong>
                    {ruleId}
                  </p>
                </label>
              </Col>
            ) : (
              <Col md="6">
                <label>
                  <p>
                    <strong>QID: </strong>
                    {qid}
                  </p>
                </label>
              </Col>
            )}
          </Row>
        )}
        {isBulk ? (
          <Row>
            <Col>
              <Card>
                <CardBody className="mt-3">
                  <p>
                    Select the assignee and due date for the selected
                    vulnerabilities. Or click Unassign button to remove the
                    assignments.
                    <br />
                    <br />
                    Note: If the selected findings are already assigned to some
                    user, the association will be removed, unassigned, and then
                    assigned to this new assignee.
                  </p>
                  {renderFormFields()}
                </CardBody>
              </Card>
            </Col>
          </Row>
        ) : (
          renderFormFields()
        )}
        {error && <div className="text-danger">{error}</div>}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={loading}>
          Cancel
        </Button>
        <Button
          color="secondary"
          onClick={handleUnassign}
          disabled={loading}
          className="mr-2"
        >
          Unassign
        </Button>
        {loading ? (
          <CircleSpinner size={20} color="#0665a9" loading={true} />
        ) : (
          <Button
            color="primary"
            onClick={handleAssign}
            disabled={
              loading ||
              !assignee ||
              (assignee?.id === parseInt(finding?.assigned_member_profile) &&
                dueDate?.getTime() === findingDueDate?.getTime()) // Disable if both are unchanged
            }
          >
            Assign
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default AssigneeModal;
